/**
 * Klebt der Chat am Ende? — EIN Besitzer für diese Frage. (28.09.2026, Ticket 2730)
 *
 * ## Woher das kommt
 *
 * Martin scrollt im Verlauf nach oben, um nachzulesen, während eine Antwort
 * entsteht — und die Ansicht reißt ihn immer wieder ans Ende zurück. Er
 * kommt nicht zum Fertiglesen. Gewünscht: nur dann ans Ende, wenn er
 * ohnehin unten war; sonst die Stelle halten und höchstens einen Knopf
 * zeigen, der nach unten führt.
 *
 * ## Was im Code stand
 *
 * „War er unten?" wurde an jeder Stelle NEU gemessen, im Moment, in dem
 * Inhalt dazukam — mit drei verschiedenen Schwellen (80, 120, 200 Punkte).
 * Etliche Stellen fragten gar nicht: Die Sprechtaste zog bei JEDEM
 * Textstück bedingungslos ans Ende, ebenso Werkzeugkarten, Schritte und der
 * Abgleich nach einem Verbindungsabriss. Jeder dieser Sprünge hielt das Ende
 * dann 0,9 s lang fest, und das nächste Stück startete das Festhalten neu.
 *
 * Auch dort, wo gefragt wurde, kam man nicht weg: Die ersten 120 Punkte nach
 * oben galten noch als „unten", und das nächste Stück zog zurück, bevor der
 * Daumen weiter war.
 *
 * ## Die Regel
 *
 * „Klebt am Ende" ist ein ZUSTAND, kein Messwert beim Einfügen. Er ändert
 * sich nur, wenn der Verlauf scrollt:
 *
 *   - Steht die Ansicht am Ende (Abstand ≤ AM_ENDE_PX), klebt sie.
 *   - Bewegt sie sich nach oben, ohne am Ende zu stehen, klebt sie nicht mehr.
 *   - Nach unten, aber noch nicht am Ende: bleibt, wie es war.
 *
 * Neuer Inhalt ändert den Zustand nie. Wächst die Antwort unten, während er
 * liest, bleibt „klebt nicht" stehen, egal wie weit das Ende wegrückt.
 *
 * ⚠️ Warum die RICHTUNG zählt und nicht nur der Abstand: Die App scrollt
 *    selbst — ans Ende ziehen, ältere Nachrichten oben einfügen —, aber nie
 *    nach oben. Nach oben bewegt nur der Mensch die Ansicht, oder der
 *    Browser, wenn unten Inhalt verschwindet; dann steht sie aber am Ende.
 *
 * ⚠️ Solange der Leser die Fläche bewegt — Finger auf dem Glas (auch ruhig), Nachlaufen nach dem
 *    Loslassen, Ausfedern am Rand, Rad —, zieht nichts nach (lib/scroll-ruhe.ts, EINE Regel für alle
 *    Schreiber). Danach holt `folgen` nach, falls er dann noch am Ende steht.
 *    Bis pre.3605 stand hier eine eigene Frist von 250 ms nach `touchstart`/`touchmove`/Rad: Das
 *    Nachlaufen NACH dem Loslassen und ein ruhig liegender Daumen galten als „niemand da“. Im echten
 *    WebKit (tests/browser/scroll-iphone-webkit.mts, Fall b) schrieb `halte` so zehnmal mitten in
 *    Nachlauf, Ausfedern und unter dem Daumen, je +21/+42 Punkte — am iPhone bricht jedes dieser
 *    Schreiben das Nachlaufen ab: das Zuckeln (Journal 02.10.: `art=hoehe delta=+42` alle 0,3 s).
 */

/**
 * So nah am Ende gilt die Ansicht als „am Ende".
 *
 * Klein, weil schon ein paar Punkte nach oben „ich lese" heißen. Nicht null,
 * weil der Browser Bruchteile rechnet und das Festhalten unten zum Neuzeichnen
 * einmal einen Punkt über dem Ende steht (siehe `ansEnde`).
 */
import { gesteuert } from "./ruckel-schalter.js";
import { MIN_SCHRITT_PX, nachRuhe, nutzerScrolltGerade, schreibeScrollTop } from "./scroll-ruhe.js";

export const AM_ENDE_PX = 4;

/** So lange hält `folgen` das Ende fest, wenn danach noch etwas wächst. */
const FOLGEN_MS = 900;

/** Die Klasse, unter der das Blatt die weiche Scroll-Animation abschaltet. */
const HAFTET_KLASSE = "haftet";

interface ChatFolgen {
  /** Steht der Verlauf am Ende — oder wurde er zuletzt dorthin gestellt? */
  klebt(): boolean;
  /** Neuer Inhalt unten: nur nachziehen, wenn er ohnehin unten war. */
  folgen(): void;
  /**
   * Seine eigene Handlung — Senden, Knopf „nach unten", Chat öffnen: immer
   * ans Ende, und ab jetzt klebt der Verlauf wieder.
   */
  ansEnde(dauerMs?: number): void;
}

/**
 * Den Verlauf `chat` beobachten. `nachScroll` läuft nach jeder Neubestimmung —
 * dort hängt der schwebende Knopf, der zeigt, ob unten etwas wartet.
 */
export function erzeugeChatFolgen(
  chat: HTMLElement,
  nachScroll: () => void = () => undefined,
): ChatFolgen {
  let klebt = true;
  let letzteOben = chat.scrollTop;
  let haltenBis = 0;
  let haeltGerade = false;
  /** Schlüssel des wartenden Nachholens (lib/scroll-ruhe.ts: je Schlüssel höchstens ein Auftrag). */
  const NACHHOLEN = {};

  const abstand = (): number => chat.scrollHeight - chat.scrollTop - chat.clientHeight;
  const endeOben = (): number => Math.max(0, chat.scrollHeight - chat.clientHeight);
  const ziehe = (): void => {
    schreibeScrollTop(chat, endeOben(), "chat-folgen");
  };

  /** Sobald der Leser die Fläche loslässt: nachziehen, falls er dann noch am Ende steht. */
  const nachholen = (): void => {
    nachRuhe(chat, NACHHOLEN, () => {
      if (klebt) halte(FOLGEN_MS);
    }, "chat-folgen");
  };

  /**
   * Das Ende einige Bilder lang festhalten. Ein einmaliges Setzen reicht
   * nicht: Markdown, Bilder und Karten wachsen nach dem Einfügen noch.
   * Endet, sobald es mehrere Bilder ruhig steht, die Zeit um ist, der
   * Verlauf nicht mehr klebt oder der Leser ihn bewegt — nie gegenscrollen;
   * im letzten Fall holt es nach, sobald er loslässt.
   */
  const halte = (dauerMs: number): void => {
    haltenBis = Math.max(haltenBis, performance.now() + dauerMs);
    if (nutzerScrolltGerade(chat)) {
      nachholen();
      return;
    }
    chat.classList.add(HAFTET_KLASSE);
    ziehe();
    if (haeltGerade) return;
    haeltGerade = true;
    let ruhig = 0;
    const schritt = (): void => {
      const bewegt = nutzerScrolltGerade(chat);
      if (!klebt || bewegt || performance.now() >= haltenBis || ruhig >= 4) {
        haeltGerade = false;
        chat.classList.remove(HAFTET_KLASSE);
        if (bewegt && klebt) nachholen();
        return;
      }
      if (Math.abs(chat.scrollTop - endeOben()) >= MIN_SCHRITT_PX) {
        ziehe();
        ruhig = 0;
      } else {
        ruhig++;
      }
      requestAnimationFrame(schritt);
    };
    requestAnimationFrame(schritt);
  };

  // Ruckel-Test (Ticket 2912): „Chat: Auto-Folgen aus“ — was von selbst ans Ende schreibt, ruht (nachgeholt beim Ausschalten).
  const folgen = gesteuert("folgen", (): void => {
    if (!klebt) return;
    halte(FOLGEN_MS);
  }, true);

  // Ruckel-Test (Ticket 2912): bei „Scroll-Messfühler aus“ ruhen die Scroll- und Gestenwachen des Verlaufs.
  chat.addEventListener(
    "scroll",
    gesteuert("wachen", () => {
      const oben = chat.scrollTop;
      if (abstand() <= AM_ENDE_PX) klebt = true;
      else if (oben < letzteOben) klebt = false;
      letzteOben = oben;
      nachScroll();
    }),
    { passive: true },
  );
  /*
   * Bilder, die nachladen, machen die Blase höher, ohne dass jemand
   * „folgen" ruft. `load` steigt nicht auf — deshalb in der Einfangphase.
   */
  chat.addEventListener("load", folgen, true);
  /*
   * Die Tastatur am Telefon, eine Drehung, ein wachsender Verfasser: Der
   * Verlauf wird kleiner, das Ende rutscht unter den Rand. Klebte er, bleibt
   * er dran.
   */
  if (typeof ResizeObserver !== "undefined") new ResizeObserver(gesteuert("wachen", folgen, true)).observe(chat);

  return {
    klebt: () => klebt,
    folgen,
    ansEnde: (dauerMs = FOLGEN_MS) => {
      klebt = true;
      /*
       * Seine Handlung — aber nie gegen eine laufende Bewegung: Läuft die Fläche noch nach (er tippt
       * auf „nach unten“, während ein Wisch ausläuft), springt sie, sobald sie steht (≤ 150 ms).
       */
      if (nutzerScrolltGerade(chat)) {
        haltenBis = Math.max(haltenBis, performance.now() + dauerMs);
        nachholen();
        return;
      }
      /*
       * WKWebView-Zeichenstoß (2026-07-02): Steht der Verlauf schon am Ende, erzwingt ein Punkt darüber
       * ein Scroll-Ereignis und damit ein Neuzeichnen. Dort bleibt er; ein Punkt liegt innerhalb von
       * AM_ENDE_PX und gilt als am Ende. ⚠️ Nur noch, wenn er schon unten steht — sonst ist der Sprung
       * selbst das Scroll-Ereignis, und der Stoß war ein zweites, sinnloses Schreiben. Ob es den Stoß am
       * Gerät heute noch braucht, ist auf dem Server nicht prüfbar (WKWebView); er kommt nur aus
       * ausdrücklichen Handlungen (Senden, Knopf, Chat öffnen), nie beim Lesen.
       */
      chat.classList.add(HAFTET_KLASSE);
      if (abstand() <= AM_ENDE_PX) schreibeScrollTop(chat, Math.max(0, endeOben() - 1), "chat-folgen", true);
      halte(dauerMs);
    },
  };
}
